import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react'

export const UNSAVED_CHANGES_ID = {
  FORM: 'checkout-form',
  EDITOR: 'checkout-editor',
} as const

type UnsavedChangesContextType = {
  dirtyStates: Record<string, boolean>
  registerDirtyState: (id: string, isDirty: boolean) => void
  unregisterDirtyState: (id: string) => void
  combinedIsDirty: boolean
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined)

export const UnsavedChangesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dirtyStates, setDirtyStates] = useState<Record<string, boolean>>({})

  const registerDirtyState = useCallback((id: string, isDirty: boolean) => {
    setDirtyStates((prev) => ({
      ...prev,
      [id]: isDirty,
    }))
  }, [])

  const unregisterDirtyState = useCallback((id: string) => {
    setDirtyStates((prev) => {
      const newState = { ...prev }
      delete newState[id]
      return newState
    })
  }, [])

  const combinedIsDirty = useMemo(
    () => Object.values(dirtyStates).some((isDirty) => isDirty),
    [dirtyStates]
  )

  const value = {
    dirtyStates,
    registerDirtyState,
    unregisterDirtyState,
    combinedIsDirty,
  }

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  )
}

export const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext)
  if (!context) {
    throw new Error('useUnsavedChanges must be used within UnsavedChangesProvider')
  }
  return context
}

/**
 * カスタムフック：isDirty を自動的に Context に登録
 *
 * @param isDirty - ダーティ状態
 * @param id - 一意な識別子（UNSAVED_CHANGES_ID の定数を使用すること）
 *
 * @example
 * import { useRegisterUnsavedChanges, UNSAVED_CHANGES_ID } from './UnsavedChangesContext'
 *
 * useRegisterUnsavedChanges({ isDirty, id: UNSAVED_CHANGES_ID.FORM })
 * useRegisterUnsavedChanges({ isDirty, id: UNSAVED_CHANGES_ID.EDITOR })
 */
export const useRegisterUnsavedChanges = ({ isDirty, id }: { isDirty: boolean; id: string }) => {
  const { registerDirtyState, unregisterDirtyState } = useUnsavedChanges()

  useEffect(() => {
    registerDirtyState(id, isDirty)
  }, [id, isDirty, registerDirtyState])

  useEffect(() => {
    return () => {
      unregisterDirtyState(id)
    }
  }, [id, unregisterDirtyState])
}
